/**
 * What an action will set off, in words, under the server's summary in the
 * step-up dialog (IP-96) - so the person typing a code reads its consequence,
 * not only its facts. Drawn from the server's summary alone; nothing is
 * re-derived here. An action with nothing to add has no note.
 *
 * The accept of a bank statement is the one whose effects reach past the
 * Core: its summary carries `downstream`, the counts statementAcceptReadiness
 * read (packages/domain/src/cash/import.js), and the note says them, since
 * the matches and payout confirmations the accept makes are sent to LIV.
 *
 * IP-111 (design 2026-10-02 section 7): the ten pricing and cash decisions of
 * D-D are step-up too, and each says what the code commits - from the
 * shapes of packages/domain/src/pricing/summaries.js (the run's facts:
 * fundId, valuationDate, navPerUnit, sellingPrice, buyingPrice,
 * buyingPriceWithExitFee, checkCode) and cash/summaries.js (MATCH_APPROVE's
 * dealingDate, input, basis, bookClosed, clearanceDeadline; LINE_CLEAR's
 * order and dealingDate; LINE_RETURN's transactionRef and slaExpiresOn, or
 * unallocated when no order waits on the cheque (IP-130);
 * RECEIPT_REFUND's amount and refundReference; LINE_MANUAL_CLEAR's direction
 * and amount, IP-157). Amounts are the server's
 * decimal strings, shown as they are.
 *
 * IP-103: a file's upload, accept and reject speak of its class and kind -
 * the key `fileClass` or `fileClass:loadKind` (summary.loadKind, which
 * intake/summaries.js carries for the four classes that have one; NAV_VALUES
 * and BANK_STATEMENT have none). An unknown key keeps the general words for
 * an upload and a reject, and has no accept note.
 */
import { STORED_CLASSES, WM_CLASSES, acceptedBySystem } from '@lfoc/domain/intake/kinds.js';

import { dayShort } from '../businessDay/businessDay.js';


/**
 * The step-up dialog's layer (StepUpDialog.jsx): above the File Uploads
 * drawer (1000) and the reject-reason dialog antd nests inside it (1200, its
 * useZIndex: a container inside a container adds 100 to the parent's 1100),
 * below antd's messages (2000 and up).
 */
export const STEP_UP_Z_INDEX = 1300;

const plural = (n, one) => (Number(n) === 1 ? '1 ' + one : String(n ?? 0) + ' ' + one + 's');

/** The keys of a summary the note says in words, which the dialog's table need not repeat. */
export const NOTED_KEYS = Object.freeze(['downstream']);

const lkr = (amount) => 'LKR ' + amount;
const on = (date) => (date ? ' for ' + date : '');
const positive = (amount) => typeof amount === 'string' && /[1-9]/.test(amount) && !amount.startsWith('-');

/** The register key of a file: its class, and its kind when it has one (IP-103). */
export function loadKeyOf(summary) {
  if (!summary?.fileClass) return null;
  return summary.loadKind ? summary.fileClass + ':' + summary.loadKind : summary.fileClass;
}

/** What a file does once accepted, by its key: a clause after "Accepting this file" / "Once accepted, the file". */
const ACCEPT_EFFECT = Object.freeze({
  NAV_VALUES: (s) => 'builds a NAV run for each fund in the file' + on(s.businessDate)
    + '. Each run is checked, and its approval publishes its price.',
  'CALENDAR:INITIAL': () => 'adds its dates to the business calendar, recording each date it changes with this file.'
    + ' A date anything was already dealt on cannot change.',
  'CALENDAR:DAILY': () => 'adds its future dates to the business calendar or changes their day type, recording each change with this file.',
  'UNIT_PRICES:INITIAL': () => 'stores its prices as price history for dates that have no price. They are history only:'
    + ' no transaction is ever priced or dealt on them.',
  'HOLDINGS:INITIAL': (s) => 'opens each lot in it as a holding in the register' + (s.businessDate ? ' as at ' + s.businessDate : '')
    + ', for clients who hold nothing in the fund yet. Nothing is sent to LIV.',
  'HOLDINGS:DAILY': (s) => 'compares its holdings with the register' + (s.businessDate ? ' as at ' + s.businessDate : '')
    + ' and lists the breaks. It never changes a balance.',
  'TRANSACTIONS:INITIAL': () => 'stores its rows as transaction history. The register\'s units and balances do not change.',
  'TRANSACTIONS:DAILY': (s) => 'posts its transactions to the register' + on(s.businessDate)
    + ' - units, lots and balances - skipping any the Core already has. Nothing is sent to LIV.',
});

/** The kind of file in words, for a reject. */
const FILE_NOUN = Object.freeze({
  NAV_VALUES: 'NAV file', BANK_STATEMENT: 'bank statement', CALENDAR: 'calendar file', UNIT_PRICES: 'unit price file',
  HOLDINGS: 'holdings file', TRANSACTIONS: 'transactions file', WM_HOLDINGS: 'WM holdings file', WM_TRANSACTIONS: 'WM transactions file',
});

/** The store-only classes (migration 0014) and the WM files (0026): accepting one keeps it, and sets nothing off. */
const STORED_EFFECT = () => 'is kept as uploaded, for download. Nothing is loaded from it into the Core.';
const isStored = (fileClass) => STORED_CLASSES.includes(fileClass) || WM_CLASSES.includes(fileClass);

const INITIAL_ONLY = ' This is an initial load: only a person allowed to load initial data can accept it.';

function statementAccept(summary) {
  const counts = summary.downstream ?? null;
  return 'Accepting imports the statement' + (counts?.statementDate ? ' for ' + counts.statementDate : '') + '. '
    + (counts
      ? 'Its credits are matched against the ' + plural(counts.subscriptionsAwaitingFunds, 'subscription') + ' awaiting funds, '
        + 'and its debits confirm any of the ' + plural(counts.payoutItemsSent, 'payout item') + ' already sent'
        // IP-184: a server that counts them says how many redemptions its debits may pay.
        + (counts.redemptionsAwaitingPayout === undefined ? '. '
          : ' or are matched to the ' + plural(counts.redemptionsAwaitingPayout, 'redemption') + ' awaiting payout. ')
      : 'Its credits are matched against subscriptions awaiting funds, and its debits confirm payouts already sent. ')
    + 'Each match and confirmation is sent to LIV.';
}

function uploadNote(summary) {
  const key = loadKeyOf(summary);
  const effect = ACCEPT_EFFECT[key] ?? (isStored(summary?.fileClass) ? STORED_EFFECT : null);
  // Migration 0016: a price file is accepted by the system once it passes; each NAV run's approval is the second person.
  if (acceptedBySystem(summary) && effect) {
    return 'After your code, the file goes straight to the file store. It is scanned for malware and checked. Once it passes, the file '
      + effect(summary);
  }
  return 'After your code, the file goes straight to the file store. It is scanned for malware and checked, and another person accepts it before it is used.'
    + (effect ? ' Once accepted, the file ' + effect(summary) : '')
    + (summary?.loadKind === 'INITIAL' ? INITIAL_ONLY : '');
}

/** INTAKE_UPLOAD_BATCH: one code for every file listed; each is still its own batch, scanned and accepted on its own. */
function uploadBatchNote(summary) {
  const count = summary?.fileCount ?? summary?.files?.length;
  return 'One code uploads ' + (count ? 'all ' + count + ' files' : 'every file') + ' listed. After your code, each goes straight to the file store, '
    + 'is scanned for malware and checked, and is accepted by another person before it is used - each on its own.'
    + (summary?.files?.some((f) => acceptedBySystem(f)) ? ' A NAV file is accepted as soon as it passes: another person approves its prices.' : '')
    + (summary?.files?.some((f) => f.loadKind === 'INITIAL') ? INITIAL_ONLY : '');
}

function acceptNote(summary) {
  if (summary?.fileClass === 'BANK_STATEMENT') return statementAccept(summary);
  if (isStored(summary?.fileClass)) return 'Accepting keeps this file as uploaded, for download. Nothing is loaded from it into the Core.';
  const effect = ACCEPT_EFFECT[loadKeyOf(summary)];
  return effect ? 'Accepting this file ' + effect(summary) : null;
}

function rejectNote(summary) {
  const noun = FILE_NOUN[summary?.fileClass] ?? 'file';
  return 'Rejecting turns this ' + noun + ' away for good: nothing in it is used. The same file can be uploaded again.';
}

const prices = (s) => 'NAV per unit ' + s.navPerUnit + ', selling ' + s.sellingPrice + ', buying ' + s.buyingPrice
  + (s.buyingPriceWithExitFee ? ', buying with exit fee ' + s.buyingPriceWithExitFee : '')
  + (s.sellingPriceWithEntryFee ? ', selling with front-end fee ' + s.sellingPriceWithEntryFee : '');

const runOf = (s) => (s?.fundId ?? 'the fund') + '\'s prices' + on(s?.valuationDate);

/**
 * IP-176 (design 2026-10-13 sections 2.3 and 2.4): a claim or an approval
 * that leaves its order owing (summary.part) is a part payment - its amount
 * of the expected amount, what earlier parts brought in or have claimed, and
 * what is still owed after it (owedAfter); the transaction waits for the
 * rest, and LIV hears nothing until the money is complete. A claim's
 * owedAfter already counts the other claims waiting (cash/claims.js
 * claimable), so it says so; an approval's does not.
 */
function partSentence(s, amount, { netOfPending = false } = {}) {
  const received = positive(s.receivedAmount);
  const pending = positive(s.pendingAmount);
  return 'Part payment: ' + lkr(amount) + (s.expectedAmount ? ' of ' + s.expectedAmount : '')
    + (received ? ', with ' + lkr(s.receivedAmount) + ' already received' : '')
    + (pending ? (received ? ' and ' : ', with ') + lkr(s.pendingAmount) + ' claimed and awaiting approval' : '')
    + '. ' + lkr(s.owedAfter) + ' will still be owed' + (pending && netOfPending ? ' once those claims are approved' : '')
    + '; the transaction waits for the rest.';
}

/** The part that completes an order paid in parts says so: the money is complete with it (IP-176). */
const completesParts = (s) => (positive(s.receivedAmount) ? ' With the ' + lkr(s.receivedAmount) + ' already received, it completes the money.' : '');

/**
 * IP-184: a debit claimed for a redemption's payout - the whole payout, or a
 * part of it (one account of several); a debit never carries an excess.
 */
function payoutClaimNote(s) {
  const debit = 'Claiming proposes this debit of ' + lkr(s.lineAmount ?? s.amount) + ' as ';
  if (s.part === true) {
    return debit + 'part of the payout of ' + (s.transactionRef ?? 'the redemption') + ' (' + lkr(s.expectedAmount) + ' in all): '
      + lkr(s.owedAfter) + ' of it will still be unpaid. Another person must approve the claim.';
  }
  return debit + 'the payout of ' + (s.transactionRef ?? 'the redemption') + ' (' + lkr(s.expectedAmount) + ').'
    + ' Another person must approve the claim; the payout is then confirmed and LIV is told.';
}

/** IP-184: approving a debit's claim - a part, or the payout confirmed in full and LIV told. */
function payoutApproveNote(s) {
  const head = 'Approving matches this debit of ' + lkr(s.amount) + ' to the payout of ' + (s.transactionRef ?? 'the redemption') + '.';
  if (s.part === true) return head + ' ' + lkr(s.owedAfter) + ' of it is still unpaid; LIV is told once it is paid in full.';
  return head + ' The payout is then paid in full and confirmed, and LIV is told.';
}

/** MATCH_CLAIM: a claim for the whole amount (and any excess), or a part of it (IP-176). */
function matchClaimNote(s) {
  if (s.payout === true) return payoutClaimNote(s);
  const credit = 'Claiming proposes this credit of ' + lkr(s.lineAmount ?? s.amount);
  if (s.part === true) {
    return credit + ' as part of the money for ' + (s.transactionRef ?? 'the transaction') + '. '
      + partSentence(s, s.allocatedAmount ?? s.lineAmount ?? s.amount, { netOfPending: true }) + ' Another person must approve the claim.';
  }
  return credit + ' as the money for ' + (s.transactionRef ?? 'the transaction')
    + ' (expected ' + lkr(s.expectedAmount) + ').' + completesParts(s) + ' Another person must approve the claim before the transaction moves.'
    + (positive(s.excessAmount) ? ' The ' + lkr(s.excessAmount) + ' over the expected amount stays with the fund until it is refunded by hand.' : '');
}

/** MATCH_APPROVE: what the approval deals - FINAL on a cleared line (T5, T9 at once into a closed book), or the wait for clearance (T6). */
function matchApproveNote(s) {
  if (s.payout === true) return payoutApproveNote(s);
  if (s.part === true || s.orderStateAfter === 'AWAITING_FUNDS') {
    return 'Approving matches ' + lkr(s.amount) + ' to ' + (s.transactionRef ?? 'the transaction') + '. ' + partSentence(s, s.amount)
      + ' LIV is told only once the whole amount is in.';
  }
  const head = 'Approving matches ' + lkr(s.amount) + ' to ' + (s.transactionRef ?? 'the transaction') + '.' + completesParts(s) + ' ';
  const excess = positive(s.excessAmount) ? ' The ' + lkr(s.excessAmount) + ' over the expected amount stays with the fund until it is refunded by hand.' : '';
  if (s.basis === 'FINAL') {
    // IP-126, IP-101: into a closed book the order was not reviewed in time for, it rolls (or its roll is held) - the server's sentence.
    let closed = ': that book is already closed, so the transaction is frozen for pricing at once.';
    if (s.rollMessage) closed = ': that book is already closed. ' + s.rollMessage;
    return head + 'The money is cleared, so the transaction deals on ' + s.dealingDate
      + (s.bookClosed ? closed : '.')
      + ' LIV is told of the match.' + excess;
  }
  return head + 'The money is still in clearing: the transaction keeps its date of ' + s.dealingDate + ' until the bank clears it'
    + (s.clearanceDeadline ? ', which must be by ' + s.clearanceDeadline : '') + '. LIV is told of the match.' + excess;
}

function lineClearNote(s) {
  const head = 'Recording ' + s.clearedOn + ' as the date the bank cleared this credit.';
  if (!s.order) return head + ' No transaction waits on it.';
  return head + ' The transaction ' + s.order.transactionRef + ' it pays then deals on ' + s.dealingDate + ', and LIV is told.'
    + (s.rollMessage ? ' ' + s.rollMessage : '');
}

/**
 * @param {string} actionType
 * @param {object|null} summary step-up/start's summary
 * @returns {string|null}
 */
export function stepUpNote(actionType, summary) {
  const s = summary ?? {};
  switch (actionType) {
    case 'INTAKE_UPLOAD':
      return uploadNote(s);
    case 'INTAKE_UPLOAD_BATCH':
      return uploadBatchNote(s);
    case 'INTAKE_ACCEPT':
      return acceptNote(s);
    case 'INTAKE_REJECT':
      return rejectNote(s);

    case 'PRICE_OVERRIDE':
      return 'Overriding records your reason against ' + (s.checkCode ?? 'the check') + ' on ' + runOf(s) + '. If nothing else fails,'
        + ' the run can then be approved - by another person - which publishes its price.';
    case 'PRICE_APPROVE':
      return 'Approving publishes ' + runOf(s) + ': ' + prices(s) + '. Transactions waiting on the date are priced and can be approved,'
        + ' and LIV is told the price.';
    case 'PRICE_REJECT':
      return 'Rejecting ends this run of ' + runOf(s) + ': no price comes from it. A corrected NAV file must be uploaded to price the date.';

    case 'MATCH_CLAIM':
      return matchClaimNote(s);
    case 'MATCH_APPROVE':
      return matchApproveNote(s);
    case 'MATCH_REJECT':
      return 'Rejecting turns this claim down: the ' + (s.direction === 'DBIT' ? 'debit' : 'credit')
        + ' is unreconciled again and can be claimed for another transaction.';
    case 'LINE_UNRECONCILE':
      // IP-184: a debit's automatic match to a redemption's payout.
      return s.payout === true
        ? 'Unreconciling undoes the automatic match of this debit to the payout of ' + (s.transactionRef ?? 'the redemption')
          + ': the debit is unmatched again, and the payout is unpaid until a debit is matched to it.'
          + (s.payoutState === 'CONFIRMED' ? ' LIV was already told the payout was confirmed, and is not told of the undo.' : '')
        : null;
    case 'LINE_CLEAR':
      return lineClearNote(s);
    case 'LINE_RETURN':
      if (s.unallocated) {
        return 'Recording the cheque as returned on ' + s.returnedOn + '. No transaction waits on it, so only the credit moves: it is closed as returned,'
          + ' and is never claimed or refunded.';
      }
      return 'Recording the cheque as returned on ' + s.returnedOn + ': its match is undone, '
        + (s.transactionRef ? 'the transaction ' + s.transactionRef : 'its transaction') + ' waits for its money again' + (s.slaExpiresOn ? ' until ' + s.slaExpiresOn : '') + ', and LIV is told to ask the investor to pay again.';
    case 'RECEIPT_REFUND':
      return 'Recording that the bank sent ' + lkr(s.amount) + ' back to the account it came from (reference ' + s.refundReference + ').'
        + ' The credit is closed as refunded and can never be matched.';
    case 'LINE_MANUAL_CLEAR':
      return 'Clearing this ' + (s.direction === 'DBIT' ? 'debit' : 'credit') + ' of ' + lkr(s.amount) + ' by hand: it leaves the'
        + ' outstanding lines and is never matched, claimed or refunded. Your reason is kept on the line.';
    case 'DAY_END_RUN':
      return dayEndRunNote(s);
    default:
      return null;
  }
}

/**
 * IP-195, IP-200: what Run day end does with the day's transactions waiting for approval, from
 * the server's counts (day-end/carry.js summariseDayEndRun) - then every holding is checked and
 * the day closed. A day run again (resume) decided them when it first ran.
 */
function dayEndRunNote(s) {
  const day = dayShort(s.businessDate);
  const then = 'Day end then checks every holding and closes ' + day + '.';
  if (s.resume) {
    return 'Day end runs again for ' + day + '. Its transactions waiting for approval were carried over or moved when it first ran. '
      + 'It checks every holding and closes the day.';
  }
  const waiting = Number(s.waitingForApproval ?? 0);
  if (!waiting) return 'No transactions on ' + day + ' are waiting for approval. ' + then;
  const parts = [];
  if (Number(s.carriedOver)) {
    parts.push(plural(s.carriedOver, 'reconciled transaction') + (Number(s.carriedOver) === 1 ? ' is' : ' are') + ' carried over, keeping '
      + day + '\'s price: approve ' + (Number(s.carriedOver) === 1 ? 'it' : 'them') + ' before the next price is approved');
  }
  if (Number(s.moved)) {
    parts.push(plural(s.moved, 'transaction') + ' not reconciled ' + (Number(s.moved) === 1 ? 'moves' : 'move') + ' to ' + dayShort(s.movedTo)
      + ' and ' + (Number(s.moved) === 1 ? 'is' : 'are') + ' priced again there');
  }
  return plural(waiting, 'transaction') + ' on ' + day + (waiting === 1 ? ' is' : ' are') + ' waiting for approval: ' + parts.join('; ') + '. ' + then;
}

/**
 * What the person should weigh before typing the code, shown as a warning - or null. Run day
 * end (IP-200): a bank statement not loaded leaves its transactions unreconciled, and they move
 * to the next dealing date (the owner: warn, do not block).
 */
export function stepUpWarning(actionType, summary) {
  if (actionType !== 'DAY_END_RUN' || summary?.resume) return null;
  const loaded = Number(summary?.statements?.loaded ?? 0);
  const expected = Number(summary?.statements?.expected ?? 0);
  if (!expected || loaded >= expected) return null;
  return loaded + ' of ' + plural(expected, 'bank statement') + ' loaded: transactions not reconciled move to the next dealing date.';
}
